/* eslint-disable max-lines */
// NOTE: All datatable code in this file is soon to be deprecated (when the "Todo" page is merged with "Consultation")
// TODO: Remove eslint-disable when the file is cleaned up.
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ColumnDropdown from './ColumnDropDown';
import UploadDialog from './UploadDialog';
import { debounce } from '../../../js/eventDelaying';
import CheckboxSelectField from '../CheckboxSelectField/CheckboxSelectField';
import FilterDialog from '../FilterDialog';
import { applyTableSearch, startTableSearch, updateTableFilters } from 'FRONTEND/reducers/tableSlice';
import useSize from 'FRONTEND/utils/hooks/useSize';
import i18n from 'FRONTEND/utils/i18n';
import { SCREEN_XS_PX } from 'LEGACY/constants';
import { createDataTables } from 'LEGACY/datatables';

const SEARCH_INPUT_HEIGHT = '40px';
const ListActionBar = ({
  title,
  workflows,
  createLabel,
  upload,
  search,
  initialFilters = [],
}) => {
  const locale = useSelector((state) => state.user.locale);
  const dispatch = useDispatch();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [dataTable, setDataTable] = useState(null);

  const location = window.location.pathname;
  const storedSearchQuery = useSelector((state) => state.table.tableSearchQuery?.[location] || '');
  const [localSearchQuery, setLocalSearchQuery] = useState(storedSearchQuery); // Used to keep input fluid and fast during debouncing.
  const storedTableFilters = useSelector((state) => state.table.tableFilters?.[location]);
  const [filters, setFilters] = useState(storedTableFilters || initialFilters);
  const mainContainer = document.getElementById('actionBar-container');
  const actionBarWidth = useSize({ current: mainContainer });
  const screenXS = actionBarWidth < SCREEN_XS_PX;
  const debouncedSearch = useMemo(() =>
    debounce((searchValue) => {
      dispatch(applyTableSearch({ tableName: location, searchQuery: searchValue }));
      if (dataTable) {
        dataTable.search(searchValue).draw();
      }
    }, 300), [dataTable]);

  useEffect(() => {
    const initializedDataTable = createDataTables();

    if (!initializedDataTable) {
      // No DataTable in the DOM (React table only) — still initialise filters from persisted/defaults.
      const loadedFilters = storedTableFilters ?? initialFilters.map((filter) => ({
        ...filter,
        checked: filter.defaultValue ?? true,
      }));
      setFilters(loadedFilters);
      dispatch(updateTableFilters({ tableName: location, filters: loadedFilters }));
      return;
    }

    const tableState = initializedDataTable.state.loaded();
    const isClientSide = !initializedDataTable.page.info().serverSide;
    // Load filter values: persisted Redux state first, then DataTables' own saved state, then defaults.
    const loadedFilters = initialFilters.map((filter) => {
      const persistedFilter = storedTableFilters?.find((f) => f.condition === filter.condition);
      const checked = persistedFilter ? persistedFilter.checked : (tableState && filter.condition in tableState) ? tableState[filter.condition] : (filter.defaultValue ?? true);
      return { ...filter, checked };
    });
    setFilters(loadedFilters);
    dispatch(updateTableFilters({ tableName: location, filters: loadedFilters })); // Sync loaded filters to global state
    setDataTable(initializedDataTable);
    // TODO: REMOVE ALL DATA TABLE STATE MANAGEMENT.
    dispatch(applyTableSearch({ tableName: location, searchQuery: tableState ? tableState.search.search : '' }));
    // For client-side tables, apply initial filters immediately
    if (isClientSide) {
      loadedFilters.forEach((filter) => {
        if (!filter.checked) {
          const splitCondition = filter.condition.split('==');
          initializedDataTable.column(`[data-data="${splitCondition[0]}"]`).search(splitCondition[1]);
        }
      });
      initializedDataTable.draw();
    }

    return () => {
      initializedDataTable.state.save();
    };
  }, []);
  const handleDropDownClick = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const formattedOptions = useMemo(() => {
    return filters.map(({ label, condition, checked }) => ({
      label,
      value: label,
    }));
  }, [filters]);
  const formattedValues = useMemo(() => {
    return filters.map(({ label, checked }) => checked && label).filter(Boolean);
  }, [filters]);
  const handleSearch = (newSearchString) => {
    setLocalSearchQuery(newSearchString);
    dispatch(startTableSearch());
    debouncedSearch(newSearchString);
  };
  const handleFilterChange = (newValues) => {
    const newFilters = filters.map((filter) => ({
      ...filter,
      checked: newValues.includes(filter.label),
    }));

    setFilters(newFilters);
    dispatch(updateTableFilters({ tableName: location, filters: newFilters }));

    if (dataTable) {
      // Mutate DataTables' own state object (mirrors what handleCheckboxChange used to do
      // per-filter) so saved/reloaded DataTables state agrees with the new checked values.
      const state = dataTable.state.loaded() || {};
      newFilters.forEach((filter) => {
        state[filter.condition] = filter.checked;
        // Sync DOM / DataTables state per filter, but don't call setFilters/dispatch again per filter.
        const splitCondition = filter.condition.split('==');
        document.querySelectorAll('.dataTables_custom_filter').forEach((el) => {
          if (el.dataset.condition === filter.condition) {
            el.setAttribute('data-checked', String(filter.checked));
          }
        });
        if (filter.checked) {
          dataTable.column(`[data-data="${splitCondition[0]}"]`).search('');
        } else {
          dataTable.column(`[data-data="${splitCondition[0]}"]`).search(splitCondition[1]);
        }
      });
      dataTable.state.save();
      // Single draw after all column searches are queued, instead of one draw per filter
      // (avoids redundant AJAX round-trips for server-side tables).
      dataTable.draw();
    }
  };
  const renderSearchBox = () => {
    return (
      <TextField
        label={i18n._('Search')}
        margin='dense'
        variant='outlined'
        sx={{ height: SEARCH_INPUT_HEIGHT }}
        value={localSearchQuery}
        className='SearchBox'
        onChange={(e) => handleSearch(e.target.value)}
        InputProps={{
          sx: { height: SEARCH_INPUT_HEIGHT },
          autoFocus: true,
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton size='large'>
                {localSearchQuery ? <CloseIcon onClick={() => handleSearch('')} /> : <SearchIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          sx: {
            height: SEARCH_INPUT_HEIGHT,
            top: '-6px',
            '&.Mui-focused': {
              top: 0,
            },
          },
        }}
      />
    );
  };
  const icon = workflows[0]?.id === 'patients-create' ? <PersonAddIcon /> : <AddIcon />;
  return (
    <div className='actionBar' id='actionBar-container'>
      {workflows?.length > 0 && (
        <div className='actionBar-workflows'>
          {workflows?.length === 1 && (
            <Button
              component='button'
              type='button'
              name='workflow'
              href={workflows[0].href}
              data-pjax
              variant='contained'
              startIcon={icon}
              sx={{
                // Href property makes this button an <a> at the root. Those tags are styled by bootstrap. We need to override those styles.
                '&:hover, &:focus': {
                  color: (theme) => theme.palette.getContrastText(theme.palette.primary.main),
                },
              }}
            >
              {!screenXS && workflows[0].label}
            </Button>
          )}
          {workflows?.length > 1 && (
            <>
              <Button
                onClick={handleDropDownClick}
                variant='contained'
                startIcon={icon}
                endIcon={<DownIcon />}
              >
                {!screenXS && createLabel}
              </Button>
              <Menu
                open={Boolean(menuAnchorEl)}
                anchorEl={menuAnchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                disableAutoFocusItem
              >
                {workflows.map(({ label, href }) => (
                  href &&
                    <MenuItem
                      key={label}
                      component='button'
                      type='button'
                      href={href}
                      data-pjax
                      onClick={handleMenuClose}
                    >
                      {label}
                    </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </div>
      )}
      {upload && (
        <UploadDialog
          href={upload.href}
          label={upload.label}
        />
      )}
      {/* Hidden divs for DataTables to read filter metadata (data-condition, data-default) */}
      {filters && filters.map(({ label, condition, defaultValue, checked }) => (
        <div
          key={label}
          className='dataTables_custom_filter'
          data-condition={condition}
          data-default={defaultValue}
          data-initialized={Boolean(dataTable)}
          data-checked={checked} // Set initial checked state for DataTables to read on first load
          style={{ display: 'none' }}
        />
      ))}
      {screenXS ? (
        <>
          {search && renderSearchBox()}
          <Stack direction='row' justifyContent='end' flexGrow='1'>
            {filters.length > 0 && (
              <FilterDialog
                formattedOptions={formattedOptions}
                formattedValues={formattedValues}
                onFilterChange={handleFilterChange}
                hasColumnDropdown
                dataTable={dataTable}
              />
            )}
          </Stack>
        </>
      ) : (
        <Stack direction='row' justifyContent='space-between' width='100%' alignItems='center'>
          {title && (
            <Typography className='actionBar-title' variant='h2'>{typeof title === 'string' ? title : title[locale]}</Typography>
          )}
          {search && renderSearchBox()}
          <Stack direction='row' alignItems='center' justifyContent='end'>
            {/* Select field for regular filters */}
            {formattedOptions.length > 0 && (
              <CheckboxSelectField
                options={formattedOptions}
                selectedValues={formattedValues}
                placeholder={i18n._('No filter applied')}
                selectedPlaceholder={i18n._('Filters applied')}
                onChange={handleFilterChange}
                showSelectAll={false}
              />
            )}
            <ColumnDropdown datatable={dataTable} label={i18n._('Columns')} />
          </Stack>
        </Stack>
      )}
    </div>
  );
};
export default ListActionBar;
